import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  getSupportEmail,
  getDomainConfig,
} from "../utils/domainConfig";
import {
  registrationService,
  SessionInfo,
} from "../services/registrationService";
import { gtmTrackConversion, gtmTrackEvent } from "../hooks/useGTM";
import apiClient from "../services/apiClient";

/**
 * RegistrationSuccess Page
 *
 * Displayed after successful registration (Stripe Checkout or Direct Activation).
 * Shows email verification message and resend button.
 *
 * Flow A (Stripe):
 * 1. User completes Stripe Checkout payment
 * 2. Stripe redirects to /registration/success?session_id={CHECKOUT_SESSION_ID}
 *
 * Flow B (Direct Activation):
 * 1. User completes registration form
 * 2. Backend activates account immediately
 * 3. Frontend stores email, subdomain, and event_id in sessionStorage, then redirects to /registration/success?direct=true
 */
export const RegistrationSuccess: React.FC = () => {
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const conversionTracked = useRef(false);
  const resendAttemptCount = useRef(0);
  const appDomain = getDomainConfig().appDomain;

  // Parse URL parameters
  const params = new URLSearchParams(window.location.search);
  const isDirectActivation = params.get("direct") === "true";
  const sessionId = params.get("session_id");
  const subdomain = sessionStorage.getItem("registrationSubdomain") || params.get("subdomain");
  const eventId = sessionStorage.getItem("registrationEventId") || params.get("event_id");
  // Clean up after reading
  sessionStorage.removeItem("registrationSubdomain");
  sessionStorage.removeItem("registrationEventId");

  // Internal-link entry attribution (landing `ref` param), set on /register.
  // Attached to the conversion event as `signup_entry` so we can see which landing
  // entry point produced the registration without polluting source via utm_*.
  // Read-once via lazy initializer (like `email` below) so the value is stable
  // across renders and survives React.StrictMode's double render in dev — a
  // top-level read+remove during render would lose it on the second pass and
  // also flip effect dependencies.
  const [signupEntry] = useState<string | undefined>(() => {
    const v = sessionStorage.getItem("registrationRef") || undefined;
    sessionStorage.removeItem("registrationRef");
    return v;
  });
  // Plan/method captured on the /register submit; attached to the sign_up
  // conversion so it carries plan_name / billing_interval / method dimensions.
  const [conversionMeta] = useState<{
    method?: string;
    plan_id?: string;
    plan_name?: string;
    billing_interval?: string;
  }>(() => {
    try {
      const raw = sessionStorage.getItem("registrationConversionMeta");
      sessionStorage.removeItem("registrationConversionMeta");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  // Read email from sessionStorage (preferred) with URL param fallback for backward compatibility.
  // sessionStorage avoids exposing PII in URL (server logs, browser history, Referer header).
  const [email] = useState<string | null>(() => {
    const stored = sessionStorage.getItem('registrationEmail');
    if (stored) {
      sessionStorage.removeItem('registrationEmail');
      return stored;
    }
    // TODO: Remove after 2026-07-01 — backward compat only, email should come from sessionStorage
    return params.get("email");
  });

  // 5b: registration from a trial invite (set by /register) — no automatic
  // proforma, so the "pay the proforma" next step does not apply. Read-once.
  const [fromTrialInvite] = useState<boolean>(() => {
    const stored = sessionStorage.getItem("registrationTrialInvite") === "true";
    sessionStorage.removeItem("registrationTrialInvite");
    return stored;
  });

  // Track GTM conversion event (once per page load)
  // For direct activation: fires immediately with known has_trial=false, trial_days=0.
  // For Stripe flow: deferred — fires after sessionInfo resolves so has_trial/trial_days
  // contain actual values from the Stripe session (AC-8 HIGH-1 fix).
  useEffect(() => {
    if (conversionTracked.current) return;

    if (isDirectActivation) {
      conversionTracked.current = true;

      // Event #9: registration_success_view for direct activation
      gtmTrackConversion("registration_success_view", {
        activation_type: "direct",
        has_trial: false,
        trial_days: 0,
        signup_entry: signupEntry,
        ...conversionMeta,
      });

      // Faza 1 migration: keep old event alongside new for 30 days
      gtmTrackConversion("direct_registration", {
        session_id: sessionId,
        subdomain: subdomain,
        page_location: window.location.href,
      });

      // For direct flow: push CompleteRegistration immediately (eventId from URL params)
      if (eventId) {
        window.dataLayer?.push({
          event: "CompleteRegistration",
          event_id: eventId,
        });
      }
    }
    // For Stripe flow, the conversion event is deferred to fetchSessionInfo below.
  }, [isDirectActivation, subdomain, sessionId, eventId, signupEntry, conversionMeta]);

  // Fetch session info on mount (only for Stripe flow)
  // Also fires registration_success_view with actual has_trial/trial_days values
  // and pushes CompleteRegistration with eventId from session-info response.
  useEffect(() => {
    const fetchSessionInfo = async () => {
      if (isDirectActivation) {
        return; // Direct activation handled in the effect above
      }

      if (!sessionId) {
        return;
      }

      try {
        const info = await registrationService.getSessionInfo(sessionId);
        setSessionInfo(info);

        // Fire registration_success_view only after sessionInfo resolves so
        // has_trial and trial_days reflect actual Stripe session values (AC-8).
        if (!conversionTracked.current) {
          conversionTracked.current = true;

          gtmTrackConversion("registration_success_view", {
            activation_type: "stripe",
            has_trial: info.hasTrial,
            trial_days: info.trialDays,
            signup_entry: signupEntry,
            ...conversionMeta,
          });

          // Faza 1 migration: keep old event alongside new for 30 days
          gtmTrackConversion("trial_start", {
            session_id: sessionId,
            subdomain: subdomain,
            page_location: window.location.href,
          });
        }

        // For Stripe flow: push CompleteRegistration with eventId from session-info
        // The event_id must match the one sent via CAPI for proper deduplication
        if (info.eventId) {
          window.dataLayer?.push({
            event: "CompleteRegistration",
            event_id: info.eventId,
          });
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Failed to fetch session info:", error);
        }
        // Silently fail — don't show trial message on error.
        // Fire registration_success_view without trial info as fallback.
        if (!conversionTracked.current) {
          conversionTracked.current = true;
          gtmTrackConversion("registration_success_view", {
            activation_type: "stripe",
            has_trial: false,
            trial_days: 0,
            signup_entry: signupEntry,
            ...conversionMeta,
          });
          gtmTrackConversion("trial_start", {
            session_id: sessionId,
            subdomain: subdomain,
            page_location: window.location.href,
          });
        }
      }
    };

    fetchSessionInfo();
  }, [isDirectActivation, sessionId, subdomain, signupEntry, conversionMeta]);

  // Cooldown timer for resend button (AC-10)
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setTimeout(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Handle resend verification email (AC-10)
  const handleResend = useCallback(async () => {
    if (!email || resendLoading || resendCooldown > 0) return;

    setResendLoading(true);
    setResendMessage(null);
    setResendError(null);

    // Event #10: resend_verification with attempt_number
    resendAttemptCount.current += 1;
    gtmTrackEvent("resend_verification", {
      attempt_number: resendAttemptCount.current,
    });

    try {
      await apiClient.post("/auth/resend-verification", { email });
      setResendMessage("Link weryfikacyjny został wysłany ponownie.");
      setResendCooldown(60);
    } catch (error: unknown) {
      const err = error as { response?: { status?: number } };
      if (err.response?.status === 429) {
        setResendError("Poczekaj chwilę przed ponownym wysłaniem.");
      } else {
        setResendError("Wystąpił błąd podczas wysyłania. Spróbuj ponownie.");
      }
    } finally {
      setResendLoading(false);
    }
  }, [email, resendLoading, resendCooldown]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-primary-50 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8 md:p-12">
        {/* Email Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center">
            <svg
              className="w-12 h-12 text-primary-700"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
        </div>

        {/* AC-9: Email verification message */}
        <h1 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
          Konto prawie gotowe!
        </h1>

        <p className="text-lg text-center text-gray-600 mb-8">
          {email ? (
            <>
              Sprawdź skrzynkę <strong>{email}</strong> i kliknij link
              weryfikacyjny, aby aktywować konto.
            </>
          ) : (
            <>
              Sprawdź swoją skrzynkę email i kliknij link weryfikacyjny, aby
              aktywować konto.
            </>
          )}
        </p>

        {/* Next Steps */}
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-primary-900 mb-3">
            Następne kroki:
          </h2>
          <ul className="space-y-2 text-primary-800">
            <li className="flex items-start">
              <svg
                className="w-5 h-5 text-primary-700 mr-2 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>
                <strong>Otwórz email weryfikacyjny</strong> i kliknij link
                potwierdzający
              </span>
            </li>
            <li className="flex items-start">
              <svg
                className="w-5 h-5 text-primary-700 mr-2 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span>
                <strong>Zaloguj się</strong> do{" "}
                <strong>{appDomain}</strong> używając hasła ustawionego
                przy rejestracji
              </span>
            </li>
            {isDirectActivation && email && !fromTrialInvite && (
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-700 mr-2 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  <strong>Opłać fakturę proforma</strong> - wysłana na
                  adres <strong>{email}</strong>
                </span>
              </li>
            )}
            {sessionInfo?.hasTrial && !isDirectActivation && (
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-700 mr-2 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  <strong>Okres próbny {sessionInfo.trialDays} dni</strong> -
                  masz pełny dostęp do wszystkich funkcji systemu bez opłat
                </span>
              </li>
            )}
          </ul>
        </div>

        {/* AC-10: Resend verification email button */}
        {email && (
          <div className="text-center mb-6">
            <p className="text-sm text-gray-500 mb-3">
              Nie otrzymałeś wiadomości?
            </p>

            {/* Success message */}
            {resendMessage && (
              <p className="text-sm text-green-600 mb-3" role="status">
                {resendMessage}
              </p>
            )}

            {/* Error message */}
            {resendError && (
              <p className="text-sm text-red-600 mb-3" role="alert">
                {resendError}
              </p>
            )}

            <button
              onClick={handleResend}
              disabled={resendLoading || resendCooldown > 0}
              className="inline-flex items-center bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 text-gray-700 font-medium py-2 px-4 rounded-lg transition text-sm"
            >
              {resendLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Wysyłanie...
                </>
              ) : resendCooldown > 0 ? (
                `Wyślij ponownie (${resendCooldown}s)`
              ) : (
                "Wyślij ponownie"
              )}
            </button>
          </div>
        )}

        {/* AC-11: No auto-redirect - just a static link to home page */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => (window.location.href = "/")}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition"
          >
            Wróć do strony głównej
          </button>
        </div>

        {/* Support Info */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-center text-gray-500">
            Masz pytania?{" "}
            <a
              href={`mailto:${sessionInfo?.supportEmail || getSupportEmail()}`}
              className="text-primary-700 hover:underline"
            >
              Skontaktuj się z nami
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
